import React, { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormInputDate, FormSelect, FormTextInput } from '../../../../../../../shared/components/crmFormElement/FormInput';
import { GO_DIGIT_DYNAMIC_TREATMENT_DETAILS_JSON, GO_DIGIT_MATERNITY_TYPE_OPTIONS, MATERNITY_TYPE_OPTIONS,
 ONLY_NUMBER_REGEX, VIDAL_DYNAMIC_TREATMENT_DETAILS_JSON } from '../../../../../../../shared/constants/commonConstants';
import CONTROL_CONFIG_BASED_ON_PAYER from '../../../../../../../config/formControlConfig';
import { formInputStates, ICICI_MATERNITY_TYPE_OPTIONS, MATERNITY_TYPE, OBSTETRIC_HISTORY } from '../../../../../../../shared/constants/crmConstants';
import { useForceUpdate } from '../../../../../../../shared/hooks/useForceUpdate';
import { useSetFormControlConfig } from '../../../../../../../shared/hooks/useSetFormControlConfig';
import ApplicationDetail from '../../../../../../../shared/services/applicationDetail.service';
import { RootState } from '../../../../../../../store';
import { getIsPageNameDetail, findKeyValuePair, getFormDateValue, validateNumberInput, isValidStayLength, handleUserFocus } from '../../../../service/crmUtils';
import { PayerInitialState } from '../../../../../../../store/models/payerListModel';
import { isAfter, isBefore, startOfDay, isSameDay } from 'date-fns';

const MaternityCard = ({ isLoading, form, location: { pathname } }: any): ReactElement => {
  const [claimDetailBackup, setClaimDetailBackup] = useState<any>({});
  const [isFieldsChanged, setIsFieldsChanged] = useState(false);
  const { admissionDetails: { claimDetails: claimDetail, claimDetails: { materinity = {} } },
    newAdmission: { cardCollapse: { isMaternityOpen }, isArtemis },
  } = useSelector((state: RootState): RootState => state);
  const { deliveryDate, obstetricHistoryG, obstetricHistoryP, obstetricHistoryL, obstetricHistoryA,
    ailmentcode } = materinity || {};
    const { obstetricHistoryD, typeOfDelivery, vaccinationDate, dobOfChild, lmp, noOfDeliveries, noOfAdoptedChildren,
      noOfBiologicalChildren, typeOfMaternity } = claimDetail || {};
  const { value, name }: any = findKeyValuePair(MATERNITY_TYPE, 'code', ailmentcode) || {};
  const applicationService = ApplicationDetail.instance;

  const forceUpdate = useForceUpdate();
  const getValueBasedOnConfig = useSetFormControlConfig();
  const { payer } = useSelector((state: RootState): PayerInitialState => state.payerList);
  const payerConfig = CONTROL_CONFIG_BASED_ON_PAYER[payer.name];
  const payerConfigValues = Object.values(payerConfig || '');
  const GO_DIGIT_PAYER = payerConfigValues.some(({ options }: any): boolean => options === 'GO_DIGIT_MATERNITY_TYPE_OPTIONS');
  const showCard = Boolean(payerConfigValues?.filter(({type, hidden}: any): boolean => type === 'showNone' && !hidden)?.length);
  const stayLength = form?.getFieldValue(['stayLength']);
  const [inputStates, setInputStates] = useState(formInputStates.maternityCardForm);
  const ARTEMIS_PAYER = payerConfigValues.filter(({type}: any): boolean => type === 'ARTEMIS').length;

  useEffect((): any => {
    if (claimDetail && Object.keys(claimDetail).length) {
      const formValues = {
        obstetricHistoryG,
        obstetricHistoryP,
        obstetricHistoryL,
        obstetricHistoryA,
        obstetricHistoryD,
        typeOfDelivery,
        noOfAdoptedChildren,
        noOfBiologicalChildren,
        typeOfMaternity,
        lmp: getFormDateValue(pathname, lmp),
        noOfDeliveries,
        vaccinationDate: getFormDateValue(pathname, vaccinationDate),
        dobOfChild: getFormDateValue(pathname, dobOfChild),
        deliveryDate: getFormDateValue(pathname, deliveryDate),
        maternityType: getIsPageNameDetail(pathname) ? name : value,
      };

      const updatedFormValues = getValueBasedOnConfig(getIsPageNameDetail(pathname), formValues);
      setClaimDetailBackup(updatedFormValues);
      form.setFieldsValue(updatedFormValues);
      forceUpdate();
    }
  }, [claimDetail]);

  const onChangeObstetricHistory = (event: any, obstetricHistory: string): void => {
    const textValue = event?.target?.value;
    const replaceValue = textValue.replace(ONLY_NUMBER_REGEX, '');
    form.setFieldsValue({
      [obstetricHistory]: replaceValue,
    });
  };

  const onChangeFields = (): any => {
    setIsFieldsChanged(!isFieldsChanged);
  };

  const checkVisible = (event: any, key: any): boolean => {
    let isRequired = false;   // function to set fields dynamically
    const requiredJson = GO_DIGIT_PAYER ? GO_DIGIT_DYNAMIC_TREATMENT_DETAILS_JSON : VIDAL_DYNAMIC_TREATMENT_DETAILS_JSON;
    if (form?.getFieldValue(event) !== undefined &&
      form?.getFieldValue(event) !== null &&
      requiredJson?.[event]?.[form?.getFieldValue(event)] !== undefined) {
      const objectKeys = Object.values(requiredJson?.[event]?.[form?.getFieldValue(event)]);
      isRequired = objectKeys?.includes(key);
    }
    return isRequired;
  };

  const commonDetail = { isLoading, form, claimDetailBackup };

  useEffect((): void => {
  }, [isMaternityOpen]);

  useEffect((): void => {
    const expectedDateofDelivery = new Date(form.getFieldValue('deliveryDate'));
    const admissionDate = startOfDay(new Date(stayLength?.[0]));
    const dischargeDate = startOfDay(new Date(stayLength?.[1]));
    // clear delivery date field when doa and dod changes and delivery date already entered is not within the range
    if (isBefore(expectedDateofDelivery, admissionDate) || (isAfter(expectedDateofDelivery, dischargeDate)
      && !isSameDay(expectedDateofDelivery, dischargeDate))) {
      form.resetFields(['deliveryDate']);
    }
  }, [stayLength]);

  const disabledDateOfMaternity = (current: any): boolean => {
    current = new Date(current); // Convert 'current' to a Date object
    const admissionDate = stayLength?.[0] ? startOfDay(new Date(stayLength?.[0])) : null;
    const dischargeDate = stayLength?.[1] ? startOfDay(new Date(stayLength?.[1])) : null;
    if (!admissionDate || !dischargeDate || !isValidStayLength(stayLength)) {
      return true;
    }
    // Disable if the current date is before the admission date or after the discharge date
    return isBefore(current, admissionDate) || (isAfter(current, dischargeDate) && !isSameDay(current, dischargeDate));
  };

  return (
    (isMaternityOpen) ?
      <>
        {
          OBSTETRIC_HISTORY.map((history: string): ReactElement =>
            <FormTextInput key={history} detail={{
              ...commonDetail,
              key: `obstetricHistory${history}`,
              size: 4,
              labelClass: 'mb-0',
              onChange: (event: any): void => onChangeObstetricHistory(event, `obstetricHistory${history}`),
              required: isMaternityOpen,
              rules: showCard ? [validateNumberInput(`obstetricHistory${history}`)] : undefined,
            }} />)
        }
        <FormInputDate detail={{
          ...commonDetail,
          key: 'deliveryDate',
          size: 8,
          allowClear: false,
          labelClass: 'mb-0',
          required: isMaternityOpen,
          disabledDate: disabledDateOfMaternity,
          onFocus: (): void => handleUserFocus('deliveryDate', setInputStates, true),
          onBlur: (): void => handleUserFocus('deliveryDate', setInputStates, false),
          isShowSuggestedValue: inputStates.deliveryDate.isShowSuggestedValue,
        }} />
        <FormSelect detail ={{
          ...commonDetail,
          key: 'typeOfMaternity',
          size: 12,
          options: GO_DIGIT_PAYER ? GO_DIGIT_MATERNITY_TYPE_OPTIONS : (isArtemis && ARTEMIS_PAYER ) ?
          ICICI_MATERNITY_TYPE_OPTIONS : MATERNITY_TYPE_OPTIONS,
          required: isMaternityOpen,
          onChange: onChangeFields,
          onFocus: (): void => handleUserFocus('typeOfMaternity', setInputStates, true),
          onBlur: (): void => handleUserFocus('typeOfMaternity', setInputStates, false),
          isShowSuggestedValue: inputStates.typeOfMaternity.isShowSuggestedValue,
        }}/>
        <FormTextInput detail={{
        ...commonDetail,
        key: 'noOfBiologicalChildren',
        size: 12,
        hidden: !checkVisible('hospitalizationReason', 'noOfBiologicalChildren'),
        required: isMaternityOpen,
        rules: [validateNumberInput('noOfBiologicalChildren')],
        onFocus: (): void => handleUserFocus('noOfBiologicalChildren', setInputStates, true),
        onBlur: (): void => handleUserFocus('noOfBiologicalChildren', setInputStates, false),
        isShowSuggestedValue: inputStates.noOfBiologicalChildren.isShowSuggestedValue,
        }}/>
         <FormTextInput detail={{
        ...commonDetail,
        key: 'noOfAdoptedChildren',
        size: 12,
        hidden: !checkVisible('hospitalizationReason', 'noOfAdoptedChildren'),
        required: isMaternityOpen,
        rules: [validateNumberInput('noOfAdoptedChildren')],
        onFocus: (): void => handleUserFocus('noOfAdoptedChildren', setInputStates, true),
        onBlur: (): void => handleUserFocus('noOfAdoptedChildren', setInputStates, false),
        isShowSuggestedValue: inputStates.noOfAdoptedChildren.isShowSuggestedValue,
      }}/>
         <FormTextInput detail={{
        ...commonDetail,
        key: 'noOfDeliveries',
        size: 12,
        hidden: !checkVisible('hospitalizationReason', 'noOfDeliveries'),
        required: isMaternityOpen,
        rules: [validateNumberInput('noOfDeliveries')],
        onFocus: (): void => handleUserFocus('noOfDeliveries', setInputStates, true),
        onBlur: (): void => handleUserFocus('noOfDeliveries', setInputStates, false),
        isShowSuggestedValue: inputStates.noOfDeliveries.isShowSuggestedValue,
      }}/>
           <FormInputDate detail={{
          ...commonDetail,
          key: 'lmp',
          size: 12,
          allowClear: false,
          labelClass: 'mb-0',
          required: false,
          onFocus: (): void => handleUserFocus('lmp', setInputStates, true),
          onBlur: (): void => handleUserFocus('lmp', setInputStates, false),
          isShowSuggestedValue: inputStates.lmp.isShowSuggestedValue,
        }} />
     <FormInputDate detail={{
          ...commonDetail,
          key: 'dobOfChild',
          size: 12,
          allowClear: false,
          labelClass: 'mb-0',
          hidden: !checkVisible('typeOfMaternity', 'dobOfChild'),
          required: isMaternityOpen,
        }} />
      <FormInputDate detail={{
          ...commonDetail,
          key: 'vaccinationDate',
          size: 12,
          allowClear: false,
          labelClass: 'mb-0',
          hidden: !checkVisible('typeOfMaternity', 'vaccinationDate'),
          required: false,
        }} />
         <FormSelect detail ={{
          ...commonDetail,
          key: 'typeOfDelivery',
          size: 12,
          hidden: !checkVisible('typeOfMaternity', 'typeOfDelivery'),
          options: ['Normal Delivery', 'LSCS Delivery'],
          required: isMaternityOpen,
        }}/>
              {
          !applicationService.getIsNewAutomationEnabled() &&
          <FormSelect detail={{
            ...commonDetail,
            key: 'maternityType',
            size: 8,
            allowClear: true,
            labelClass: 'mb-0',
            required: isMaternityOpen,
            options: MATERNITY_TYPE,
          }}
          />
        }
      </> :
      <React.Fragment />
  );
};

export default withRouter(MaternityCard);
